import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryClient, withClientId } from "@/lib/queryClient";
import {
  fetchSlips,
  fetchSlipById,
  fetchStorageSummary,
  fetchAllocations,
  createSlip,
  updateSlip,
  deleteSlip,
  assignBoatToSlip,
  updateAllocationPayer,
  updateAllocationRate,
  vacateSlip,
} from "./api";
import type { Slip, SlipType, SlipWriteBody } from "@/types/storage";

export const STORAGE_KEYS = {
  slips: ["slips"] as const,
  slip: (id: string) => ["slips", id] as const,
  slipsByType: (type: SlipType) => ["slips", { type }] as const,
  summary: ["storage", "summary"] as const,
  allocations: (activeOnly: boolean) => ["storage", "allocations", { activeOnly }] as const,
};

// --- Reads ---

export const useSlips = (type?: SlipType) => {
  return useQuery({
    queryKey: type ? STORAGE_KEYS.slipsByType(type) : STORAGE_KEYS.slips,
    queryFn: () => fetchSlips(type),
  });
};

export const useSlip = (slipId: string) => {
  return useQuery({
    queryKey: STORAGE_KEYS.slip(slipId),
    queryFn: () => fetchSlipById(slipId),
    enabled: !!slipId,
  });
};

export const useStorageSummary = () => {
  return useQuery({
    queryKey: STORAGE_KEYS.summary,
    queryFn: fetchStorageSummary,
  });
};

export const useAllocations = (activeOnly = true) => {
  return useQuery({
    queryKey: STORAGE_KEYS.allocations(activeOnly),
    queryFn: () => fetchAllocations(activeOnly),
  });
};

// --- Writes ---

// Invalidates every query keyed off the slips or summary collections — call
// this after any mutation that changes which boats sit in which slips.
const invalidateAllStorage = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: STORAGE_KEYS.slips });
  qc.invalidateQueries({ queryKey: STORAGE_KEYS.summary });
  qc.invalidateQueries({ queryKey: ["storage", "allocations"] });
};

// Outbox keys (see apps/frontend/AGENTS.md "offline outbox"): paused offline
// mutations replay through the setMutationDefaults registrations below.
export const SLIP_CREATE_KEY = ["outbox", "slips", "create"] as const;
export const SLIP_UPDATE_KEY = ["outbox", "slips", "update"] as const;
export const SLIP_DELETE_KEY = ["outbox", "slips", "delete"] as const;
export const SLIP_ASSIGN_KEY = ["outbox", "slips", "assign"] as const;
export const ALLOCATION_RATE_KEY = ["outbox", "storage-allocations", "rate"] as const;
export const SLIP_VACATE_KEY = ["outbox", "slips", "vacate"] as const;

queryClient.setMutationDefaults(SLIP_CREATE_KEY, { mutationFn: createSlip });
queryClient.setMutationDefaults(SLIP_UPDATE_KEY, {
  mutationFn: (vars: { id: string; body: SlipWriteBody }) => updateSlip(vars.id, vars.body),
});
queryClient.setMutationDefaults(SLIP_DELETE_KEY, { mutationFn: deleteSlip });
queryClient.setMutationDefaults(SLIP_ASSIGN_KEY, {
  mutationFn: (vars: {
    slipId: string;
    boatId: string;
    monthlyRate?: number;
    customerId?: string;
  }) => assignBoatToSlip(vars.slipId, vars.boatId, vars.monthlyRate, vars.customerId),
});
queryClient.setMutationDefaults(ALLOCATION_RATE_KEY, {
  mutationFn: (vars: { allocationId: string; monthlyRate: number | null }) =>
    updateAllocationRate(vars.allocationId, vars.monthlyRate),
});
queryClient.setMutationDefaults(SLIP_VACATE_KEY, { mutationFn: vacateSlip });

export const useCreateSlip = () => {
  const qc = useQueryClient();
  const mutation = useMutation<Slip, Error, SlipWriteBody & { id: string }>({
    mutationKey: SLIP_CREATE_KEY,
    mutationFn: createSlip,
    onSuccess: () => invalidateAllStorage(qc),
  });
  return {
    ...mutation,
    mutate: (body: SlipWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(withClientId(body), options),
    mutateAsync: (body: SlipWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync(withClientId(body), options),
  };
};

export const useUpdateSlip = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: SLIP_UPDATE_KEY,
    mutationFn: (vars: { id: string; body: SlipWriteBody }) => updateSlip(vars.id, vars.body),
    onSuccess: (data) => {
      invalidateAllStorage(qc);
      qc.invalidateQueries({ queryKey: STORAGE_KEYS.slip(data.id) });
    },
  });
};

export const useDeleteSlip = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: SLIP_DELETE_KEY,
    mutationFn: (id: string) => deleteSlip(id),
    onSuccess: () => invalidateAllStorage(qc),
  });
};

export const useAssignBoat = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: SLIP_ASSIGN_KEY,
    mutationFn: (vars: {
      slipId: string;
      boatId: string;
      monthlyRate?: number;
      customerId?: string;
    }) => assignBoatToSlip(vars.slipId, vars.boatId, vars.monthlyRate, vars.customerId),
    onSuccess: (data) => {
      invalidateAllStorage(qc);
      qc.invalidateQueries({ queryKey: STORAGE_KEYS.slip(data.id) });
      // Boats may carry a denormalized "current slip" reference; refresh them too.
      qc.invalidateQueries({ queryKey: ["boats"] });
    },
  });
};

export const useUpdateAllocationRate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ALLOCATION_RATE_KEY,
    mutationFn: (vars: { allocationId: string; monthlyRate: number | null }) =>
      updateAllocationRate(vars.allocationId, vars.monthlyRate),
    onSuccess: () => invalidateAllStorage(qc),
  });
};

export const useUpdateAllocationPayer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { allocationId: string; customerId: string | null }) =>
      updateAllocationPayer(vars.allocationId, vars.customerId),
    onSuccess: () => invalidateAllStorage(qc),
  });
};

export const useVacateSlip = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: SLIP_VACATE_KEY,
    mutationFn: (slipId: string) => vacateSlip(slipId),
    onSuccess: (data) => {
      invalidateAllStorage(qc);
      qc.invalidateQueries({ queryKey: STORAGE_KEYS.slip(data.id) });
      qc.invalidateQueries({ queryKey: ["boats"] });
    },
  });
};
